/** Public DOM contract only. It grants no native or authenticated privileges. */
export const DESKTOP_BRIDGE_VERSION = 1;
const MAX_TEXT = 32_000;
const MAX_FILES = 20;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const SESSION = new RegExp(`^web:${UUID}$`, 'i');
const ID = new RegExp(`^${UUID}$`, 'i');
export interface DesktopAcceptance {
  signal: AbortSignal;
  accept: (response: Response) => void;
  reject: (error: unknown) => void;
}
export function acceptedChatRequest(start: (acceptance: DesktopAcceptance) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => finish(Error('desktop-request-cancelled'));
    const finish = (error?: unknown) => { signal.removeEventListener('abort', abort); error ? reject(error) : resolve(); };
    if (signal.aborted) return finish(Error('desktop-request-cancelled'));
    signal.addEventListener('abort', abort, { once: true });
    try { start({ signal, accept: r => finish(r.ok ? undefined : Error(`chat-request-rejected-${r.status}`)), reject: e => finish(e ?? Error('chat-request-rejected')) }); }
    catch (error) { finish(error); }
  });
}
/** The real transport calls this only for its exact pending chat request. */
export async function fetchDesktopAcceptedRequest(input: RequestInfo | URL, init: RequestInit | undefined, acceptance: DesktopAcceptance): Promise<Response> {
  // Desktop cancellation owns admission until response headers. Once the
  // server accepts a turn, normal SDK Stop/navigation owns its live stream.
  const admission = new AbortController();
  const abort = () => admission.abort();
  acceptance.signal.addEventListener('abort', abort, { once: true });
  try {
    if (acceptance.signal.aborted) throw Error('desktop-request-cancelled');
    const response = await globalThis.fetch(input, { ...init, signal: init?.signal ? AbortSignal.any([init.signal, admission.signal]) : admission.signal });
    if (acceptance.signal.aborted) throw Error('desktop-request-cancelled');
    acceptance.accept(response);
    return response;
  } catch (error) { acceptance.reject(error); throw error; }
  finally { acceptance.signal.removeEventListener('abort', abort); }
}
/** Exact per-request marker survives delayed SDK body preparation and aborts. */
export function createDesktopChatAcceptance() {
  const pending = new Map<string, { acceptance: DesktopAcceptance; cleanup: () => void }>();
  const header = 'x-eddy-desktop-composer-request';
  return {
    send(dispatch: (headers: Record<string, string>) => void | Promise<void>, signal: AbortSignal) {
      return acceptedChatRequest(acceptance => {
        if (pending.size >= 8) throw Error('desktop-send-capacity');
        const id = crypto.randomUUID();
        const cleanup = () => { pending.delete(id); signal.removeEventListener('abort', cleanup); };
        pending.set(id, { acceptance, cleanup });
        signal.addEventListener('abort', cleanup, { once: true });
        try { void Promise.resolve(dispatch({ [header]: id })).catch(error => { cleanup(); acceptance.reject(error); }); }
        catch (error) { cleanup(); acceptance.reject(error); }
      }, signal);
    },
    fetch(input: RequestInfo | URL, init?: RequestInit) {
      const headers = new Headers(init?.headers);
      const id = headers.get(header);
      if (!id) return globalThis.fetch(input, init);
      const entry = pending.get(id);
      if (!entry) return Promise.reject(Error('desktop-request-unrecognized'));
      entry.cleanup();
      headers.delete(header);
      return fetchDesktopAcceptedRequest(input, { ...init, headers }, entry.acceptance);
    },
  };
}
interface Composer {
  input: HTMLInputElement;
  draft: (text: string, signal: AbortSignal) => Promise<void>;
  send: (signal: AbortSignal) => Promise<void>;
}
interface Request {
  version: number; documentId: string; requestId: string; sessionId: string;
  op: string; deadline: number; text?: string; count?: number;
}
const fileHandoffs = new WeakMap<HTMLInputElement, ReturnType<typeof createDesktopComposerBridge>>();
let mountedBridge: ReturnType<typeof createDesktopComposerBridge> | undefined;
let mountedDocument: Document | undefined;
const documentOwners = new WeakMap<Document, string>();
interface Registration { session: string; composer: Composer; doc: Document; owner?: string; unregister?: () => void }
const registrations = new Set<Registration>();
function detach(registration: Registration) {
  const unregister = registration.unregister; registration.unregister = undefined; unregister?.();
}
export function registerDesktopComposer(session: string, composer: Composer) {
  const doc = composer.input.ownerDocument;
  const registration: Registration = { session, composer, doc, owner: documentOwners.get(doc) };
  registrations.add(registration);
  if (mountedDocument === doc) registration.unregister = mountedBridge?.register(session, composer);
  return () => { registrations.delete(registration); detach(registration); };
}
/** Account/session teardown must also retire callbacks from still-mounted inputs. */
export function clearDesktopComposers(doc: Document) {
  for (const registration of registrations) if (registration.doc === doc) { detach(registration); registrations.delete(registration); }
}
export function desktopComposerFiles(session: string, input: HTMLInputElement, count: number, upload: (signal?: AbortSignal) => Promise<void>) {
  const owner = fileHandoffs.get(input);
  return (owner ?? mountedBridge)?.files(session, input, count, upload) ?? upload();
}
export function mountDesktopComposerHost(doc: Document, open: (session: string) => void, owner: string) {
  mountedBridge?.dispose();
  for (const registration of registrations) detach(registration);
  const bridge = createDesktopComposerBridge(doc, open);
  mountedBridge = bridge; mountedDocument = doc; documentOwners.set(doc, owner);
  for (const registration of registrations) {
    if (registration.doc !== doc) continue;
    // Unbound registrations belong to this document's first verified owner.
    // Once bound, callbacks can survive replacement only for that exact owner.
    if (registration.owner !== undefined && registration.owner !== owner) { registrations.delete(registration); continue; }
    registration.owner = owner;
    registration.unregister = bridge.register(registration.session, registration.composer);
  }
  return () => { bridge.dispose(); if (mountedBridge === bridge) { mountedBridge = undefined; mountedDocument = undefined; } };
}
export function createDesktopComposerBridge(doc: Document, open: (session: string) => void) {
  let documentId = crypto.randomUUID();
  const root = doc.createElement('div');
  root.hidden = true;
  const advertise = () => root.setAttribute('data-eddy-desktop-bridge', JSON.stringify({ version: 1, documentId, origin: doc.location.origin, capabilities: ['draft', 'upload', 'send-accepted'] }));
  advertise();
  doc.documentElement.append(root);
  const composers = new Map<string, Set<Composer>>();
  const seen = new Set<string>();
  let disposed = false;
  let active: { request: Request; abort: AbortController; timer: ReturnType<typeof setTimeout>; composer?: Composer } | undefined;
  const ack = (request: Request, status: string, error?: string) => {
    if (disposed || active?.request !== request || active.abort.signal.aborted || Date.now() >= request.deadline) return;
    root.setAttribute('data-eddy-desktop-ack', JSON.stringify({ version: 1, documentId: request.documentId, requestId: request.requestId, sessionId: request.sessionId, status, ...(request.documentId !== documentId ? { nextDocumentId: documentId } : {}), ...(error ? { error } : {}) }));
  };
  const ready = () => {
    if (!active || active.request.op !== 'open') return;
    const choices = composers.get(active.request.sessionId);
    if (choices && choices.size > 1) ack(active.request, 'error', 'ambiguous-composer');
    else if (choices?.size === 1) ack(active.request, 'ready');
  };
  const cancel = () => {
    if (!active) return;
    clearTimeout(active.timer); active.abort.abort();
    active.composer?.input.removeAttribute('data-eddy-desktop-input');
    active = undefined;
  };
  const onRequest = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    if (disposed || typeof detail !== 'string' || detail.length > MAX_TEXT + 2048) return;
    let request: Request;
    try { request = JSON.parse(detail); } catch { return; }
    if (!request || request.documentId !== documentId || !ID.test(request.requestId) || !SESSION.test(request.sessionId) || seen.has(request.requestId)) return;
    if (!Number.isFinite(request.deadline) || request.deadline <= Date.now() || request.deadline > Date.now() + 30_000) return;
    // Open starts a new operation. Renew before its remaining stages need space;
    // every request from the retired epoch remains invalid, even with a new deadline.
    const renew = request.version === 1 && request.op === 'open' && seen.size >= 240;
    if (seen.size >= 256 && !renew) return;
    cancel();
    if (renew) { documentId = crypto.randomUUID(); seen.clear(); advertise(); }
    seen.add(request.requestId);
    const abort = new AbortController();
    active = { request, abort, timer: setTimeout(cancel, request.deadline - Date.now()) };
    if (request.version !== 1) { ack(request, 'error', 'unsupported-version'); return; }
    if (request.op === 'cancel') { cancel(); return; }
    if (request.op === 'open') { open(request.sessionId); ready(); return; }
    const choices = composers.get(request.sessionId);
    if (choices?.size !== 1) { ack(request, 'error', choices?.size ? 'ambiguous-composer' : 'composer-unavailable'); return; }
    const composer = [...choices][0]; active.composer = composer;
    if (request.op === 'files') {
      if (!Number.isInteger(request.count) || request.count! < 1 || request.count! > MAX_FILES) { ack(request, 'error', 'invalid-files'); return; }
      fileHandoffs.set(composer.input, bridge);
      composer.input.setAttribute('data-eddy-desktop-input', request.requestId);
      ack(request, 'prepared'); return;
    }
    let action: Promise<void>;
    if (request.op === 'draft' && typeof request.text === 'string' && request.text.length <= MAX_TEXT) action = composer.draft(request.text, abort.signal);
    else if (request.op === 'send') action = composer.send(abort.signal);
    else { ack(request, 'error', 'unsupported-operation'); return; }
    void action.then(() => ack(request, request.op === 'draft' ? 'committed' : 'accepted'), () => ack(request, 'error', request.op === 'draft' ? 'draft-failed' : 'send-rejected'));
  };
  doc.addEventListener('eddy-desktop-request', onRequest);
  const dispose = () => { if (disposed) return; cancel(); disposed = true; doc.removeEventListener('eddy-desktop-request', onRequest); root.remove(); composers.clear(); };
  const bridge = {
    dispose,
    register(session: string, composer: Composer) {
      const set = composers.get(session) ?? new Set<Composer>(); set.add(composer); composers.set(session, set); ready();
      return () => { set.delete(composer); if (!set.size) composers.delete(session); if (active?.composer === composer || active?.request.sessionId === session) cancel(); };
    },
    async files(session: string, input: HTMLInputElement, count: number, upload: (signal?: AbortSignal) => Promise<void>) {
      const armed = fileHandoffs.has(input);
      fileHandoffs.delete(input);
      const work = active;
      if (!work || work.request.op !== 'files' || work.request.sessionId !== session || work.composer?.input !== input || input.getAttribute('data-eddy-desktop-input') !== work.request.requestId) { if (!armed && !disposed) await upload(); return; }
      if (count !== work.request.count) { ack(work.request, 'error', 'file-count-mismatch'); return; }
      try { await upload(work.abort.signal); ack(work.request, 'uploaded'); }
      catch { ack(work.request, 'error', 'upload-failed'); }
      finally { if (input.getAttribute('data-eddy-desktop-input') === work.request.requestId) input.removeAttribute('data-eddy-desktop-input'); }
    },
  };
  return bridge;
}
